/**
 * Keeps the cart drawer's rewards progress bar and countdown timer in sync, and makes
 * quantity changes (+ / -) feel instant.
 *
 * Loaded once via <script src> from snippets/cart-drawer.liquid, outside the parts of
 * the drawer that Shopify swaps with innerHTML on cart updates (AJAX add-to-cart,
 * quantity changes). Script tags injected via innerHTML never execute, so the render
 * logic lives here instead of inline in the swapped markup, and re-syncs itself
 * through the theme's existing cart-update pub/sub event (assets/cart.js already
 * publishes it after every quantity/add/remove) plus one narrow, attribute-only
 * MutationObserver for the drawer's open/close state.
 */
(function () {
  if (window.__cartDrawerExtrasInit) return;
  window.__cartDrawerExtrasInit = true;

  // ---- Rewards progress bar (snippets/cart-reward-progress.liquid).
  // 1 prenda: envío Premium gratis · 2: 10% · 3: 15% · 4 o más: 20%.
  var REWARD_FILL = ['0%', '0%', '33.333%', '66.667%', '100%'];

  function renderRewards(itemCount) {
    var level = itemCount >= 4 ? 4 : itemCount;
    if (level < 0) level = 0;

    document.querySelectorAll('.cart-reward').forEach(function (el) {
      el.setAttribute('data-level', String(level));
      el.style.setProperty('--cart-reward-fill', REWARD_FILL[level]);

      el.querySelectorAll('.cart-reward__step').forEach(function (step) {
        var n = parseInt(step.getAttribute('data-step'), 10) || 0;
        step.classList.toggle('is-reached', n <= level);
      });
    });
  }

  function fetchCartAndRenderRewards() {
    fetch('/cart.js', { headers: { accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        renderRewards(cart.item_count);
      })
      .catch(function () {});
  }

  // ---- Instant quantity changes.
  //
  // Normally every click on + / - waits 300 ms (debounce), then a server round trip
  // that re-renders the whole drawer, with a spinner in between. Here the number and
  // the rewards bar change right away on click and the request goes out immediately;
  // when the server answers, the drawer is re-rendered as usual with the real prices.
  // Clicks made while a request is in flight are coalesced: only the latest wanted
  // quantity is sent next, so requests never overlap or arrive out of order.
  var qtyQueue = { busy: false, pending: null, timeout: null };

  function totalDrawerQty() {
    var sum = 0;
    document.querySelectorAll('.cart-drawer .cart-item .quantity__input').forEach(function (input) {
      var v = parseInt(input.value, 10);
      if (!isNaN(v)) sum += v;
    });
    return sum;
  }

  function sendQty(job) {
    var cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') {
      qtyQueue.busy = false;
      return;
    }
    qtyQueue.busy = true;
    clearTimeout(qtyQueue.timeout);
    // Safety net: if the request fails, don't stay blocked forever.
    qtyQueue.timeout = setTimeout(requestDone, 8000);
    cartItems.updateQuantity(job.index, job.qty, {}, job.name, job.variantId);
  }

  function requestDone() {
    clearTimeout(qtyQueue.timeout);
    qtyQueue.busy = false;
    if (!qtyQueue.pending) return false;

    var job = qtyQueue.pending;
    qtyQueue.pending = null;
    // The drawer was just re-rendered with the server's quantity: show the wanted one again.
    var input = document.getElementById('Drawer-quantity-' + job.index);
    if (input && job.qty > 0) input.value = job.qty;
    var row = document.getElementById('CartDrawer-Item-' + job.index);
    if (row && job.qty === 0) row.classList.add('is-removing');
    renderRewards(totalDrawerQty() - (job.qty === 0 && input ? parseInt(input.value, 10) || 0 : 0));
    sendQty(job);
    return true;
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var fromDrawerItems = event && event.source === 'cart-items';
      var hasPendingClick = false;
      if (fromDrawerItems) {
        // Drawer markup was just replaced: refresh the countdown text right away.
        tickTimer();
        // If the customer kept clicking, send the latest quantity instead of
        // flashing the intermediate server value in the bar.
        hasPendingClick = requestDone();
      }
      if (hasPendingClick) return;

      if (event && event.cartData && typeof event.cartData.item_count === 'number') {
        renderRewards(event.cartData.item_count);
      } else {
        fetchCartAndRenderRewards();
      }
    });
  }

  // ---- Urgency timer: starts at exactly 10:00 the first time the drawer is opened
  // in this browser tab (not from page load). After that it's allowed to count down
  // to 3 minutes, and only then jumps back up — to a random point between 6:00 and
  // 8:55 (never a round number of minutes, so it never reads e.g. "8:00" again) — so
  // it keeps ticking down for a while each time instead of resetting the moment it
  // dips below 10, while still never being able to reach zero.
  var TIMER_STORAGE_KEY = 'cartUrgencyExpiresAt';
  var TIMER_INITIAL_SECONDS = 600; // first open this session: exactly 10:00
  var TIMER_MIN_SECONDS = 180; // floor before it jumps back up: 3:00
  var timerInterval = null;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  // Random reset point: strictly between 6:00 and 8:55 (never below or above),
  // and never lands exactly on a round minute.
  function randomResetSeconds() {
    var minTotal = 361; // 6:01
    var maxTotal = 535; // 8:55
    var total = minTotal + Math.floor(Math.random() * (maxTotal - minTotal + 1));
    if (total % 60 === 0) total -= 1;
    return total;
  }

  // Reads the stored expiry. On the very first check this session there's nothing
  // stored, so it starts at exactly 10:00. From then on, once it would show 3
  // minutes or less, it's pushed back out to a random point above 6 minutes.
  function getTimerExpiry() {
    var stored;
    try {
      stored = sessionStorage.getItem(TIMER_STORAGE_KEY);
    } catch (e) {
      stored = null;
    }
    var expiry = stored ? parseInt(stored, 10) : NaN;
    var remainingSeconds = expiry ? (expiry - Date.now()) / 1000 : -1;

    if (!expiry || isNaN(expiry)) {
      expiry = Date.now() + TIMER_INITIAL_SECONDS * 1000;
      try {
        sessionStorage.setItem(TIMER_STORAGE_KEY, String(expiry));
      } catch (e) {}
    } else if (remainingSeconds <= TIMER_MIN_SECONDS) {
      expiry = Date.now() + randomResetSeconds() * 1000;
      try {
        sessionStorage.setItem(TIMER_STORAGE_KEY, String(expiry));
      } catch (e) {}
    }
    return expiry;
  }

  function tickTimer() {
    var el = document.getElementById('CartUrgencyTimerValue');
    if (!el) return;
    var expiry = getTimerExpiry();
    var remaining = Math.max(0, Math.round((expiry - Date.now()) / 1000));
    el.textContent = pad(Math.floor(remaining / 60)) + ':' + pad(remaining % 60);
  }

  function startUrgencyTimerIfOpen() {
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl || !cartDrawerEl.classList.contains('active')) return;

    tickTimer();
    if (!timerInterval) {
      timerInterval = setInterval(tickTimer, 1000);
    }
  }

  var cartDrawerRoot = document.querySelector('cart-drawer');
  if (cartDrawerRoot) {
    if (cartDrawerRoot.classList.contains('active')) startUrgencyTimerIfOpen();
    // Narrow on purpose: only the cart-drawer element's own "class" attribute,
    // no subtree. A subtree/childList observer here previously fed back into
    // itself — the render function adds/removes DOM nodes, which re-fired
    // the observer, which re-rendered, forever — and froze the tab.
    new MutationObserver(function () {
      startUrgencyTimerIfOpen();
    }).observe(cartDrawerRoot, { attributes: true, attributeFilter: ['class'] });
  }

  // Re-sync whenever the theme reports a cart change (quantity update, add to cart,
  // remove) via its existing pub/sub event — this is what actually keeps the
  // rewards bar correct after Dawn swaps part of the drawer's markup, without
  // watching the whole DOM for mutations.
  fetchCartAndRenderRewards();

  // ---- + / - buttons in the cart drawer (capture phase: runs before QuantityInput's
  // own click handler, which is bound directly on the button).
  //
  // Dawn's QuantityInput steps the native <input min="0"> and fires a debounced
  // "change"; going down to 0 is then rejected by assets/cart.js's validateQuantity()
  // (it compares 0 against data-min, which defaults to 1), so the line never got
  // removed. Handling the click here fixes both: minus-to-zero removes the line like
  // the trash button, and every change is applied instantly (see above).
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('.cart-drawer .quantity__button');
      if (!button || button.disabled) return;

      var quantityInputEl = button.closest('quantity-input');
      var input = quantityInputEl && quantityInputEl.querySelector('input');
      if (!input) return;

      var cartItems = button.closest('cart-drawer-items');
      var index = input.dataset.index;
      if (!cartItems || !index) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var base = parseInt(input.value, 10);
      if (qtyQueue.pending && qtyQueue.pending.index === index) base = qtyQueue.pending.qty;
      if (isNaN(base)) return;

      var next = base + (button.name === 'minus' ? -1 : 1);
      var max = parseInt(input.max, 10);
      if (next < 0 || (!isNaN(max) && next > max)) return;

      var row = button.closest('.cart-item');
      if (next > 0) {
        input.value = next;
        if (row) row.classList.remove('is-removing');
      } else if (row) {
        row.classList.add('is-removing');
      }
      renderRewards(totalDrawerQty() - (next === 0 ? base : 0));

      var job = {
        index: index,
        qty: next,
        name: button.name,
        variantId: input.dataset.quantityVariantId
      };
      if (qtyQueue.busy) {
        qtyQueue.pending = job;
      } else {
        sendQty(job);
      }
    },
    true
  );
})();

// ---- Kiwi Size Chart: todo en italiano, siempre (enlace y ventana de la tabla).
(function () {
  if (window.__kiwiItalianInit) return;
  window.__kiwiItalianInit = true;

  var LINK_TEXT = 'Guida alle taglie';
  // Traducciones exactas (texto completo del nodo, sin importar mayusculas) EN/ES -> IT.
  var DICT = {
    'size chart': LINK_TEXT, 'size guide': LINK_TEXT, 'sizing chart': LINK_TEXT, 'size charts': LINK_TEXT,
    'guía de tallas': LINK_TEXT, 'guia de tallas': LINK_TEXT, 'tabla de tallas': LINK_TEXT, 'tabla de medidas': LINK_TEXT,
    'size': 'Taglia', 'sizes': 'Taglie', 'talla': 'Taglia', 'tallas': 'Taglie', 'your size': 'La tua taglia',
    'chest': 'Torace', 'bust': 'Seno', 'waist': 'Vita', 'hip': 'Fianchi', 'hips': 'Fianchi',
    'length': 'Lunghezza', 'shoulder': 'Spalle', 'shoulders': 'Spalle', 'sleeve': 'Manica', 'sleeve length': 'Lunghezza manica',
    'inseam': 'Lunghezza interna gamba', 'thigh': 'Coscia', 'height': 'Altezza', 'weight': 'Peso', 'width': 'Larghezza',
    'pecho': 'Torace', 'busto': 'Seno', 'cintura': 'Vita', 'cadera': 'Fianchi', 'caderas': 'Fianchi', 'largo': 'Lunghezza',
    'hombro': 'Spalle', 'hombros': 'Spalle', 'manga': 'Manica', 'largo de manga': 'Lunghezza manica', 'altura': 'Altezza',
    'peso': 'Peso', 'ancho': 'Larghezza', 'muslo': 'Coscia', 'entrepierna': 'Lunghezza interna gamba',
    'inches': 'Pollici', 'inch': 'Pollici', 'in': 'Pollici', 'pulgadas': 'Pollici', 'centimeters': 'Centimetri',
    'centimetres': 'Centimetri', 'centímetros': 'Centimetri', 'centimetros': 'Centimetri', 'cm': 'cm',
    'close': 'Chiudi', 'cerrar': 'Chiudi', 'back': 'Indietro', 'volver': 'Indietro', 'next': 'Avanti', 'siguiente': 'Avanti',
    'measurements': 'Misure', 'measurement': 'Misura', 'medidas': 'Misure', 'body measurements': 'Misure del corpo',
    'garment measurements': 'Misure del capo', 'product measurements': 'Misure del prodotto', 'medidas del cuerpo': 'Misure del corpo',
    'how to measure': 'Come misurare', 'cómo medir': 'Come misurare', 'como medir': 'Come misurare',
    'find your size': 'Trova la tua taglia', 'find my size': 'Trova la mia taglia', 'encuentra tu talla': 'Trova la tua taglia',
    'recommended size': 'Taglia consigliata', 'talla recomendada': 'Taglia consigliata', 'we recommend': 'Ti consigliamo',
    'calculate': 'Calcola', 'calcular': 'Calcola', 'submit': 'Invia', 'enviar': 'Invia', 'reset': 'Reimposta',
    'unit': 'Unità', 'units': 'Unità', 'unidad': 'Unità', 'unidades': 'Unità', 'country': 'Paese', 'país': 'Paese', 'pais': 'Paese',
    'women': 'Donna', 'woman': 'Donna', 'mujer': 'Donna', 'mujeres': 'Donna', 'men': 'Uomo', 'man': 'Uomo', 'hombre': 'Uomo', 'hombres': 'Uomo',
    'tips': 'Consigli', 'tip': 'Consiglio', 'note': 'Nota', 'nota': 'Nota', 'notes': 'Note', 'notas': 'Note'
  };
  var ROOTS = '[class^="ks-"],[class*=" ks-"],[class*="_ks"],[class*="kiwi" i],[id^="ks-"],[id*="kiwi" i],[id*="_ks"]';
  var ATTRS = ['aria-label', 'title', 'placeholder', 'alt'];
  var timer = null;

  function translateText(str) {
    var trimmed = str.trim();
    if (!trimmed) return str;
    var key = trimmed.toLowerCase().replace(/[:.]+$/, '');
    var out = DICT[key];
    if (!out) return str;
    var suffix = /:$/.test(trimmed) ? ':' : '';
    return str.replace(trimmed, out + suffix);
  }

  function fix() {
    // El enlace, siempre en italiano.
    document.querySelectorAll('._ks_text').forEach(function (el) {
      if (el.textContent.trim() !== LINK_TEXT) el.textContent = LINK_TEXT;
      el.setAttribute('aria-label', 'Apri la guida alle taglie');
    });
    // Todo lo que Kiwi pinta (ventana de la tabla incluida).
    document.querySelectorAll(ROOTS).forEach(function (root) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        var p = node.parentElement;
        if (p && (p.tagName === 'SCRIPT' || p.tagName === 'STYLE')) continue;
        var t = translateText(node.nodeValue);
        if (t !== node.nodeValue) node.nodeValue = t;
      }
      root.querySelectorAll('[aria-label],[title],[placeholder],[alt]').forEach(function (el) {
        ATTRS.forEach(function (a) {
          var v = el.getAttribute(a);
          if (v) {
            var t2 = translateText(v);
            if (t2 !== v) el.setAttribute(a, t2);
          }
        });
      });
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(fix, 120);
  }

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('click', schedule, true);
  schedule();
})();